import { useAdCampaignDetail } from 'data/ad-campaign';
import React from 'react';
import { toast } from 'sonner';
import { EAdCampaignStatus } from 'types/ad-campaign';

import { ButtonBase, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';

import Button from 'creative-components/CustomButtons/Button';

import ActionModal from 'components/ActionModal/ActionModal';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { updateAdCampaign } from 'utils/api/ad-campaigns';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

type Props = {
  adCampaignId: string;
  variant?: 'settings' | 'banner';
};

export default ({
  adCampaignId,
  variant = 'settings',
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any; // TODO: TEMP AS ANY
  const { adCampaign, mutateAdCampaign } = useAdCampaignDetail(adCampaignId);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const isPauseAction = [
    EAdCampaignStatus.PendingMarketingProfile,
    EAdCampaignStatus.Active,
  ].includes(adCampaign.status);
  const actionLabel = isPauseAction ? 'Pause Ad' : 'Resume Ad';

  const onConfirmedPauseOrResume = async () => {
    const newStatus = isPauseAction
      ? EAdCampaignStatus.Paused
      : EAdCampaignStatus.Active;

    showLoadingIndicatorModal();
    try {
      await updateAdCampaign(adCampaign._id, { status: newStatus });
      mutateAdCampaign();

      if (newStatus === EAdCampaignStatus.Active) {
        toast.success('Your digital ad has been resumed!');
      } else {
        toast.success('Your digital ad has been paused. You may resume it at any time to continue showing it to your audience!');
      }
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      hideLoadingIndicatorModal();
      setShowConfirmation(false);
    }
  };

  const trigger = variant === 'banner' ? (
    <ButtonBase onClick={() => setShowConfirmation(true)}>
      {actionLabel}
    </ButtonBase>
  ) : (
    <Button
      style={{ marginTop: '130px' }}
      round
      color="primary"
      onClick={() => setShowConfirmation(true)}
    >
      {actionLabel}
    </Button>
  );

  return (
    <>
      {showConfirmation && (
        <ActionModal
          backgroundColor={theme.palette.offWhite.main}
          textColor={theme.palette.darkGray.main}
          icon={<LibraryAddCheckRoundedIcon />}
          onClose={() => setShowConfirmation(false)}
          title={isPauseAction ? 'Pause My Ad' : 'Resume My Ad'}
          message={isPauseAction
            ? 'Are you sure you want to pause your ad? Your ad will run until it uses up its current monthly budget and then will stop showing to your audience.'
            : 'Your monthly subscription will restart today and your ad will start showing to your audience again.'}
          buttons={[
            <ButtonBase onClick={() => setShowConfirmation(false)} className={classes.cancelButton}>
              Cancel
            </ButtonBase>,
            <ButtonBase onClick={onConfirmedPauseOrResume}>
              {actionLabel}
            </ButtonBase>,
          ]}
        />
      )}
      {trigger}
    </>
  );
};
